import type { LeitourDatabase } from '../database/db';
import { validateBibleBookOrder } from '../domain/bibleCanon';
import { createSyncBatch, type SyncMutationDraftWithBase } from './batch';
import type { SyncConflict, SyncOutboxRecord } from './types';

type LegacyPreferencePayload = {
  id?: unknown;
  homeHighlight?: { enabled?: unknown };
};

type CurrentPreferencePayload = {
  id?: unknown;
  showHomeHighlight?: unknown;
};

const recordOf = (value: unknown): Record<string, unknown> | undefined =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : undefined;

export const legacyPreferenceValue = (payload: unknown): boolean | undefined => {
  const value = recordOf(payload) as (LegacyPreferencePayload & Record<string, unknown>) | undefined;
  if (!value || value.id !== 'app' || 'showHomeHighlight' in value) return undefined;
  if (Object.keys(value).some((key) => !['id', 'homeHighlight', 'createdAt', 'updatedAt'].includes(key))) {
    return undefined;
  }
  const highlight = recordOf(value.homeHighlight);
  if (!highlight || Object.keys(highlight).some((key) =>
    !['enabled', 'title', 'text', 'reference'].includes(key))) return undefined;
  return typeof highlight?.enabled === 'boolean' ? highlight.enabled : undefined;
};

export const currentPreferenceValue = (payload: unknown): boolean | undefined => {
  const value = recordOf(payload) as (CurrentPreferencePayload & Record<string, unknown>) | undefined;
  return value?.id === 'app'
    && Object.keys(value).length === 2
    && typeof value.showHomeHighlight === 'boolean'
    ? value.showHomeHighlight
    : undefined;
};

export const userPreferenceValue = (payload: unknown): boolean | undefined =>
  currentPreferenceValue(payload) ?? legacyPreferenceValue(payload);

export const canonicalUserPreferencePayload = (
  payload: unknown
): { id: 'app'; showHomeHighlight: boolean } | { id: 'bible_book_order'; bookOrder: ReturnType<typeof validateBibleBookOrder> } | undefined => {
  const value = recordOf(payload);
  if (value?.id === 'bible_book_order'
    && Object.keys(value).length === 2
    && Array.isArray(value.bookOrder)) {
    try {
      return { id: 'bible_book_order', bookOrder: validateBibleBookOrder(value.bookOrder.map(String)) };
    } catch {
      return undefined;
    }
  }
  const showHomeHighlight = userPreferenceValue(payload);
  return showHomeHighlight === undefined ? undefined : { id: 'app', showHomeHighlight };
};

const isLegacyPreferenceMutation = (
  mutation: SyncOutboxRecord['batch']['mutations'][number]
): boolean => mutation.entityType === 'user_preference'
  && legacyPreferenceValue(mutation.payload) !== undefined;

const isEquivalentTechnicalConflict = (conflict: SyncConflict): boolean => {
  if (conflict.status !== 'open' || conflict.entityType !== 'user_preference') return false;
  const local = userPreferenceValue(conflict.localPayload);
  const remote = userPreferenceValue(conflict.remotePayload);
  return local !== undefined && remote !== undefined && local === remote;
};

const replacementStatus = (
  record: SyncOutboxRecord,
  drafts: readonly SyncMutationDraftWithBase[],
  hasUnresolvedConflict: boolean
): SyncOutboxRecord['status'] => {
  if (hasUnresolvedConflict) return 'conflicted';
  if (record.status === 'awaiting_bootstrap'
    || drafts.some(({ baseRevision }) => baseRevision.state === 'unresolved')) {
    return 'awaiting_bootstrap';
  }
  return 'pending';
};

interface PlannedReplacement {
  original: SyncOutboxRecord;
  batch?: Awaited<ReturnType<typeof createSyncBatch>>;
  status?: SyncOutboxRecord['status'];
}

/**
 * Repairs artifacts emitted by clients that still used homeHighlight inside
 * user_preference. Existing batches remain byte-for-byte immutable: they are
 * superseded and, when needed, replaced by a freshly canonicalized batch.
 */
export const recoverLegacyUserPreferenceArtifacts = async (
  db: LeitourDatabase,
  userId: string,
  now = new Date().toISOString()
): Promise<boolean> => {
  const [records, openConflicts, metadata] = await Promise.all([
    db.syncOutbox.toArray(),
    db.syncConflicts.where('status').equals('open').toArray(),
    db.syncMetadata.get('sync')
  ]);
  const legacyRecords = records
    .filter(({ status, batch }) => status !== 'superseded'
      && batch.mutations.some(isLegacyPreferenceMutation))
    .sort((left, right) => left.queueOrder - right.queueOrder
      || left.createdAt.localeCompare(right.createdAt)
      || left.batchId.localeCompare(right.batchId));
  const technicalConflicts = openConflicts.filter(isEquivalentTechnicalConflict);
  if (!legacyRecords.length && !technicalConflicts.length) return false;

  const resolvedConflictIds = new Set(technicalConflicts.map(({ id }) => id));
  const technicalRemoteValue = technicalConflicts
    .map(({ remotePayload }) => userPreferenceValue(remotePayload))
    .find((value): value is boolean => value !== undefined);
  const plans: PlannedReplacement[] = [];

  for (const record of legacyRecords) {
    const drafts: SyncMutationDraftWithBase[] = [];
    for (const mutation of record.batch.mutations) {
      if (isLegacyPreferenceMutation(mutation)) {
        const showHomeHighlight = legacyPreferenceValue(mutation.payload)!;
        const redundantTechnicalPreference = technicalRemoteValue !== undefined
          && showHomeHighlight === technicalRemoteValue;
        if (redundantTechnicalPreference) continue;
        drafts.push({
          entityType: 'user_preference',
          entityId: mutation.entityId,
          operation: mutation.operation,
          baseRevision: mutation.baseRevision,
          payload: { id: 'app', showHomeHighlight }
        });
        continue;
      }
      drafts.push({
        entityType: mutation.entityType,
        entityId: mutation.entityId,
        operation: mutation.operation,
        baseRevision: mutation.baseRevision,
        payload: mutation.payload
      } as SyncMutationDraftWithBase);
    }

    if (!drafts.length) {
      plans.push({ original: record });
      continue;
    }
    const unresolvedForRecord = openConflicts.some((conflict) =>
      !resolvedConflictIds.has(conflict.id)
      && (conflict.batchId === record.batchId
        || record.batch.mutations.some((mutation) =>
          mutation.entityType === conflict.entityType && mutation.entityId === conflict.entityId)));
    const batch = await createSyncBatch({
      deviceId: record.batch.deviceId,
      mutations: drafts,
      createdAt: record.batch.createdAt,
      supersedesBatchId: record.batchId,
      protocolVersion: metadata?.protocolVersion ?? record.batch.protocolVersion
    });
    plans.push({
      original: record,
      batch,
      status: replacementStatus(record, drafts, unresolvedForRecord)
    });
  }

  await db.transaction('rw', [
    db.settings, db.syncOutbox, db.syncConflicts, db.syncEntityStates, db.syncMetadata
  ], async () => {
    for (const conflict of technicalConflicts) {
      await db.syncConflicts.update(conflict.id, {
        status: 'resolved', resolution: 'accept_remote', resolvedAt: now
      });
      const remoteValue = userPreferenceValue(conflict.remotePayload);
      const settings = await db.settings.get('app');
      if (settings && remoteValue !== undefined) {
        await db.settings.put({ ...settings, showHomeHighlight: remoteValue, updatedAt: now });
      }
      if (conflict.remoteRevision && conflict.remotePayloadHash) {
        const cursor = (await db.syncMetadata.get('sync'))?.cursor.lastChangeId ?? '0';
        await db.syncEntityStates.put({
          entityType: 'user_preference', entityId: 'app',
          remoteRevision: conflict.remoteRevision,
          remotePayloadHash: conflict.remotePayloadHash,
          lastChangeId: cursor
        });
      }
    }

    for (const plan of plans) {
      await db.syncOutbox.update(plan.original.batchId, { status: 'superseded' });
      if (!plan.batch || !plan.status) continue;
      await db.syncOutbox.add({
        batchId: plan.batch.batchId,
        userId,
        queueOrder: plan.original.queueOrder,
        status: plan.status,
        batch: plan.batch,
        attemptCount: 0,
        createdAt: plan.batch.createdAt,
        nextAttemptAt: plan.batch.createdAt
      });
    }

    const metadata = await db.syncMetadata.get('sync');
    if (metadata?.bootstrapState === 'conflicted') {
      const remainingBootstrapConflicts = await db.syncConflicts.where('status').equals('open')
        .filter(({ origin }) => origin === 'bootstrap').count();
      if (!remainingBootstrapConflicts) {
        await db.syncMetadata.update('sync', {
          bootstrapState: 'complete', lastError: undefined,
          lastSuccessfulSyncAt: now
        });
      }
    }
  });
  return true;
};
